import { ActivePromotionResponse } from "../types/promotion.definition";
import { applyDurationDiscount } from "./duration-discount.utils";

const FREE_TRIAL_CODE = "FREE_TRIAL";

// Price after the facility's active promotion, as the server bills it: the rebate applies
// to the licence's monthly amount (already net of the duration discount), with the same
// rounding as the duration discount (discount rounded half-up to whole FCFA, then
// subtracted). A FREE_TRIAL promotion means nothing is charged. `rebate` is the rebate
// percentage applied (0 for no promotion or a free trial).
export function applyPromotion(
  monthlyAmount: number,
  promotion: ActivePromotionResponse | null | undefined
): { rebate: number; discount: number; amount: number } {
  if (promotion?.promotionCode === FREE_TRIAL_CODE) {
    return { rebate: 0, discount: monthlyAmount, amount: 0 };
  }
  const rebate = promotion?.rebate && promotion.rebate > 0 ? promotion.rebate : 0;
  return { rebate, ...applyDurationDiscount(monthlyAmount, rebate) };
}

// "YYYY-MM-DD" of a local Date (the server's LocalDate), never via toISOString (UTC).
export function toLocalDateOnly(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

// "YYYY-MM-DD" + n months, clamped to the end of the target month like Java's
// LocalDate.plusMonths (2026-01-31 + 1 month = 2026-02-28).
export function addMonthsDateOnly(dateStr: string, months: number): string {
  const [year, month, day] = dateStr.split("-").map(Number);
  const target = new Date(year, month - 1 + months, 1);
  const lastDay = new Date(target.getFullYear(), target.getMonth() + 1, 0).getDate();
  return toLocalDateOnly(new Date(target.getFullYear(), target.getMonth(), Math.min(day, lastDay)));
}

// Total of `months` billing cycles as the server bills a year (annual lump sum, or the
// "total over the year" of a monthly commitment): cycle i is due on firstDueDate + i months
// and priced with the promotion active on that date (start and end dates inclusive) —
// rebate rounded per month, a FREE_TRIAL month costs nothing, a month outside the
// promotion window costs the full monthly amount.
export function priceCycles(
  monthlyAmount: number,
  promotion: ActivePromotionResponse | null | undefined,
  firstDueDate: string,
  months: number
): number {
  let total = 0;
  for (let i = 0; i < months; i++) {
    const dueDate = addMonthsDateOnly(firstDueDate, i);
    const inWindow = !!promotion && promotion.startDate <= dueDate && dueDate <= promotion.endDate;
    total += applyPromotion(monthlyAmount, inWindow ? promotion : null).amount;
  }
  return total;
}
